import { useState } from 'react';

import {
	Button,
	Group,
	Popup,
	PopupTrigger,
	RadioGroup,
	Stack,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { Selection } from 'react-aria-components';

type ExportScope = 'current-page' | 'all' | 'selected';

interface ExportPopupProps<TItem> {
	/** Trigger element (usually a Button) */
	children: React.ReactNode;
	/** Resource name for display (e.g., 'leads', 'orders') */
	resourceName: string;
	/** Items currently visible on the page */
	currentPageItems: TItem[];
	/** Total count of all items matching current filters */
	totalCount: number;
	/** Current selection state from table */
	selectedItems: Selection;
	/** Function to extract the ID from an item */
	getItemId: (item: TItem) => string;
	/** Callback when export is triggered - receives IDs or undefined for "all" */
	onExport: (ids: string[] | undefined) => Promise<void>;
	/** Whether the export mutation is in progress */
	isPending: boolean;
	/** Optional className for the popup */
	className?: string;
}

export function ExportPopup<TItem>({
	children,
	resourceName,
	currentPageItems,
	totalCount,
	selectedItems,
	getItemId,
	onExport,
	isPending,
	className,
}: ExportPopupProps<TItem>) {
	const [exportScope, setExportScope] = useState<ExportScope>('current-page');

	const currentPageItemsCount = currentPageItems.length;

	// Calculate selected count
	// When "all" is selected, it means all items on the current page
	const selectedCount =
		selectedItems === 'all'
			? currentPageItemsCount
			: selectedItems instanceof Set
				? selectedItems.size
				: 0;

	// Calculate the number of items to export based on scope
	const getExportCount = (): number => {
		switch (exportScope) {
			case 'current-page':
				return currentPageItemsCount;
			case 'all':
				return totalCount;
			case 'selected':
				return selectedCount;
			default:
				return 0;
		}
	};

	// Get IDs based on export scope
	const getIds = (): string[] | undefined => {
		switch (exportScope) {
			case 'current-page': {
				// Return IDs of all items on current page
				return currentPageItems.map((item) => getItemId(item));
			}
			case 'selected': {
				// Return IDs of selected items
				// Selection contains IDs directly (from table's getItemId)
				if (selectedItems === 'all') {
					return currentPageItems.map((item) => getItemId(item));
				}
				if (selectedItems instanceof Set) {
					return Array.from(selectedItems).map((id) => String(id));
				}
				return undefined;
			}
			case 'all': {
				// Return undefined to export all items
				return undefined;
			}
			default:
				return undefined;
		}
	};

	const handleExport = async (closePopup: () => void) => {
		try {
			const ids = getIds();
			const exportCount = getExportCount();

			await onExport(ids);

			// Show success toast
			toast({
				message: `${exportCount} ${resourceName} exported`,
				variant: 'success',
			});

			// Dismiss the popup
			closePopup();
		} catch {
			// Error is handled by the mutation, but we can still show an error toast
			toast({
				message: `Failed to export ${resourceName}. Please try again.`,
				variant: 'error',
			});
		}
	};

	const exportOptions = [
		{
			value: 'current-page',
			label: 'Current page',
		},
		{
			value: 'all',
			label: `All ${resourceName}`,
		},
		{
			value: 'selected',
			label: `Selected: ${selectedCount} ${resourceName}`,
			isDisabled: selectedCount === 0,
		},
	];

	return (
		<PopupTrigger>
			{children}
			<Popup
				title={`Export ${resourceName}`}
				description={`Download a CSV file of the selected ${resourceName}.`}
				className={className}
				hasCloseButton>
				{(closePopup) => (
					<Stack itemsSpacing='24'>
						<RadioGroup
							value={exportScope}
							onChange={(value) => setExportScope(value as ExportScope)}
							items={exportOptions.map((option) => ({
								value: option.value,
								label: <Text size='bodySmall'>{option.label}</Text>,
								isDisabled: option.isDisabled,
							}))}
						/>
						<Group itemsSpacing='16' itemsAlignX='end' noWrap>
							<Button
								variant='secondary'
								size='large'
								isDisabled={isPending}
								onPress={closePopup}>
								Cancel
							</Button>
							<Button
								variant='filled'
								size='large'
								isLoading={isPending}
								isDisabled={isPending}
								onPress={() => handleExport(closePopup)}>
								Export
							</Button>
						</Group>
					</Stack>
				)}
			</Popup>
		</PopupTrigger>
	);
}
